import { useEffect, useMemo } from "react";
import { Spline, Trash2 } from "lucide-react";
import { Map as MapView } from "@/components/Map";
import { MapLegend } from "@/components/MapLegend";
import { observer } from "mobx-react-lite";
import { GeneralMapStore } from "./stores/GeneralMapStore";
import { GeneralMapStoreProvider, useGeneralMapStore } from "./stores/GeneralMapStoreContext";
import { ViewPerspective } from "./ViewPerspective";
import { FiltersCard } from "./Filters";
import { StructurePopUp } from "@/pages/Dashboard/Structures/StructurePopUp";
import { StructuresLegend } from "@/pages/Dashboard/Structures/StructuresLegend";
import { BlockagePopUp } from "@/pages/Dashboard/Blockages/BlockagePopUp.";
import { BlockagesLegend } from "@/pages/Dashboard/Blockages/BlockagesLegend";
import { PopUp as IAndIPopUp } from "@/components/IAndI/IAndIList/PopUp";
import { IAndILegend } from "@/components/IAndI/IAndIList/IAndILegend";
import { CUSTOM_FILTERS } from "@/config/list";

const MapWithLogic = observer(() => {
	const generalMapStore = useGeneralMapStore();
	const { mapStore } = generalMapStore;

	useEffect(() => {
		if (!mapStore.isLoaded) return;
		generalMapStore.onPerspectiveChange(generalMapStore.perspective);
	}, [mapStore.isLoaded]);

	const renderPopup = (marker: any) => {
		if (generalMapStore.perspective === "overview") {
			return (
				<StructurePopUp
					structure={marker}
					missingConnectivity={
						mapStore.connectionsStore.visible && generalMapStore.isMissingConnectivity(marker)
					}
					selectedTypeIds={mapStore.filters.selectedIds(CUSTOM_FILTERS.structureTypesIds)}
					selectedGroupIds={mapStore.filters.selectedIds(CUSTOM_FILTERS.structureGroupIds)}
					onToggleType={typeId => mapStore.filters.toggle(CUSTOM_FILTERS.structureTypesIds, typeId)}
					onToggleGroup={groupId => mapStore.filters.toggle(CUSTOM_FILTERS.structureGroupIds, groupId)}
				/>
			);
		}
		if (generalMapStore.perspective === "IAndI") {
			return <IAndIPopUp iAndI={marker} />;
		}
		return <BlockagePopUp blockage={marker} />;
	};

	return (
		<div className="flex-1 min-h-full flex mb-6 rounded-md overflow-hidden">
			<FiltersCard />
			<div className="flex-1 flex relative">
				<ViewPerspective />
				<MapView
					mapStore={mapStore}
					loader={{ isLoading: generalMapStore.isLoading }}
					enableUrlParams
					initFromUrl
					renderPopup={renderPopup}
					rightClickActions={click => {
						if (click.target.kind === "marker" && mapStore.connectionsStore.visible) {
							return [
								{
									id: "connection.create",
									label: "Add connection",
									icon: <Spline className="h-4 w-4 text-[#003BFF]" />,
									onSelect: ({ target }) => {
										if (target.kind !== "marker") return;
										mapStore.connectionsStore.selectFromMarker(target.id as Dryp.StructureId);
									},
								},
							];
						}
						if (click.target.kind === "connection") {
							return [
								{
									id: "connection.delete",
									label: "Delete connection",
									icon: <Trash2 className="h-4 w-4 text-red-500" />,
									onSelect: ({ target }) => {
										if (target.kind !== "connection") return;
										mapStore.connectionsStore.removeConnection(target.id);
									},
								},
							];
						}
						return [];
					}}
				>
					<MapLegend>
						{generalMapStore.perspective === "IAndI" ? (
							<IAndILegend />
						) : generalMapStore.perspective === "blockages" ? (
							<BlockagesLegend blockages={generalMapStore.blockageData.data?.items ?? []} />
						) : (
							<StructuresLegend structures={generalMapStore.filteredStructures} />
						)}
					</MapLegend>
				</MapView>
			</div>
		</div>
	);
});

export const Map = observer(() => {
	const generalMapStore = useMemo(() => new GeneralMapStore(), []);
	return (
		<GeneralMapStoreProvider value={generalMapStore}>
			<MapWithLogic />
		</GeneralMapStoreProvider>
	);
});
